import {
  formatToUSDate,
  formatBillingCycleDateToUser,
  formatBackendDate,
  formatBackendTime,
} from '../modules/user/helper/helperFunction'
import {USER_ROLES} from './StatusConstants'
import {getCandiTranslations} from '../helpers/translationHelper'
import moment from 'moment'

const t = getCandiTranslations()

export const PATIENT_HEADERS = [
  {
    name: 'Name',
    key: 'redirected_to',
    secondaryKeyName: 'full_name',
    redirected_to: '/patient-details',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    sortable: true,
  },
  {
    name: 'Doctor',
    selector: (row: any) => `${row?.AssignedDoctor?.full_name ?? ''}`,
    key: 'AssignedDoctor.full_name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Device',
    key: 'device_allocations',
    selector: (row: any) => {
      // only keep allocations that are still active (no de-allocation date)
      const activeAllocs =
        row.DeviceAllocations?.filter((a: any) => !a.devices_de_allocation_date) || []
      if (activeAllocs.length === 0) return '—'

      // pull each device's name (fall back to device_id) and dedupe
      const names = activeAllocs.map((a: any) => a.Device?.name || String(a.device_id))
      return Array.from(new Set(names)).join(', ')
    },
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: t.readings,
    selector: (row: any) => row?.readings,
    key: 'readings',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: t.careMins,
    selector: (row: any) => row?.care_minutes ?? 0,
    key: 'care_minutes',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: t.cycle,
    selector: (row: any) => {
      // Use same logic as renderBillingCycleStatus for consistency
      let billingCycle = row?.BillingCycles?.[0] || null

      // For active cycles, use the dates as-is without alignment
      let startDate = formatBillingCycleDateToUser(billingCycle?.cycle_start)
      let endDate = formatBillingCycleDateToUser(billingCycle?.cycle_end)
      return billingCycle ? `${startDate} - ${endDate}` : `-`
    },
    key: 'billing_cycle_status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  // {
  //   name: 'Status',
  //   key: 'status',
  //   selector: (row: any) => (row.status === 1 ? 'Active' : 'Inactive'),
  //   allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  // },
  {
    name: 'Actions',
    key: 'settings',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
]

export const DOCTOR_PAYMENT_HISTORY_HEADERS = [
  {
    name: 'Invoice #',
    key: 'invoiceNumber',
    allowedRoles: [USER_ROLES.ADMIN],
    sortable: true,
  },
  {
    name: 'Date',
    key: 'date',
    allowedRoles: [USER_ROLES.ADMIN],
    sortable: true,
  },
  {
    name: 'Customer',
    key: 'customerName',
    allowedRoles: [USER_ROLES.ADMIN],
  },
  {
    name: 'Items',
    key: 'items',
    allowedRoles: [USER_ROLES.ADMIN],
  },
  {
    name: 'Amount',
    key: 'amount',
    allowedRoles: [USER_ROLES.ADMIN],
    sortable: true,
  },
  // {
  //   name: 'Status',
  //   key: 'status',
  //   allowedRoles: [USER_ROLES.ADMIN],
  //   sortable: true,
  // },
  {
    name: 'Billing Period',
    key: 'billingPeriod',
    selector: (row: any) => row.billingPeriod || '—',
    allowedRoles: [USER_ROLES.ADMIN],
  },
  {
    name: 'Download PDF',
    key: 'downloadPdf',
    allowedRoles: [USER_ROLES.ADMIN],
  },
]

// nurse headers
export const NURSE_HEADERS = [
  // {
  //   name: 'ID',
  //   selector: (row: any) => row?.Nurse?.id,
  //   sortable: true,
  //   key: 'id',
  //   requiredPermission: 'public',
  // },
  {
    name: 'Name',
    key: 'redirected_to',
    secondaryKeyName: 'full_name',
    redirected_to: '/nurse-details',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Staff Role',
    selector: (row: any) => {
      switch (row.role_id) {
        case USER_ROLES.NURSE:
          return 'Nurse'
        case USER_ROLES.BILLING_PERSONNEL:
          return 'Billing Personnel'
        default:
          return 'Unknown'
      }
    },
    key: 'role_id',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Email Address',
    selector: (row: any) => row?.email,
    key: 'email',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Phone',
    selector: (row: any) => row?.phone_number,
    key: 'phone_number',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Status',
    selector: (row: any) => row?.status,
    key: 'status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Doctor',
    selector: (row: any) => `${row?.AssignedDoctor?.full_name}`, //TODO: Use full name
    key: 'AssignedDoctor.full_name', //TODO: First name is not always unique, use uuid?
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN], //, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Actions',
    key: 'settings',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
]

export const DOCTOR_HEADERS = [
  {
    name: 'Name',
    key: 'redirected_to',
    secondaryKeyName: 'full_name',
    redirected_to: '/doctor-details',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Devices (R-L-O)',
    // This is computed in the DataTableList component
    key: 'allocated_devices_count',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Patients',
    // This is computed in the DataTableList component
    key: 'patients_count_by_status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Staff (S-B)',
    selector: (row: any) => `${row.active_staff_count} - ?`,
    key: 'staff_count_by_role',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Subscription',
    selector: (row: any) => row, // This is overridden in the DataTableList component
    key: 'subscription_status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: '',
    key: 'settings',
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: '80px',
    center: true,
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
]

export const SUB_ADMIN_HEADERS = [
  {
    name: 'Name',
    key: 'redirected_to',
    secondaryKeyName: 'full_name',
    redirected_to: '/sub-admin-details',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Email Address',
    selector: (row: any) => row.email,
    key: 'email',
    allowedRoles: [USER_ROLES.ADMIN],
  },
  {
    name: 'Phone',
    selector: (row: any) => row.phone_number,
    key: 'phone_number',
    allowedRoles: [USER_ROLES.ADMIN],
  },
]

export const MEDICAL_ASSISTANT_HEADERS = [
  {
    name: 'Name',
    key: 'redirected_to',
    secondaryKeyName: 'full_name',
    redirected_to: '/medical-assistant-details',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Email Address',
    selector: (row: any) => row.email,
    key: 'email',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Phone',
    selector: (row: any) => row.phone_number || '—',
    key: 'phone_number',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
  {
    name: 'Status',
    selector: (row: any) => (row.status === 1 ? 'Active' : 'Inactive'),
    key: 'status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
  },
]

export const TIME_TRACKED_HEADERS = [
  {
    name: 'Tracked On',
    selector: (row: any) => (row?.from ? Date.parse(row.from) : 0),
    sortable: true,
    key: 'date',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    width: '125px',
  },
  {
    name: 'Duration',
    selector: (row: any) => row.time_spent,
    key: 'time_spent',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    width: '100px',
  },
  {
    name: 'Tracked By',
    selector: (row: any) => `${row?.TrackedBy?.full_name}`,
    key: 'TrackedBy?.full_name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    width: '125px',
  },
  {
    name: 'Patient',
    selector: (row: any) => `${row?.Patient?.full_name}`,
    key: 'Patient?.full_name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    width: '125px',
  },
  {
    name: 'Notes',
    selector: (row: any) => `${row?.description}`,
    key: 'description',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    grow: 1,
  },
  {
    name: 'Actions',
    key: 'actions',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
    width: '100px',
  },
]

export const DOCTOR_DEVICE_HEADERS = [
  {
    name: 'Device Name',
    selector: (row: any) => row.Device?.name,
    key: 'Device?.name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'IMEI',
    selector: (row: any) => row.Device?.imei,
    key: 'Device?.imei',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Ownership',
    selector: (row: any) => {
      const type = row.ownership_type || 'purchased'
      return type.charAt(0).toUpperCase() + type.slice(1)
    },
    key: 'ownership_type',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Status',
    selector: (row: any) => (row.assigned_to_patient ? 'Assigned' : 'Available'),
    key: 'assigned_to_patient',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },

  {
    name: 'Assigned To',
    selector: (row: any) => row.Device?.AllocatedDevice?.[0]?.Patient?.full_name ?? '—',
    key: 'redirected_to',
    secondaryKeyName: 'Device.AllocatedDevice[0].Patient.uuid',
    redirected_to: '/patient-details',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Assigned At',
    selector: (row: any) =>
      row.Device?.AllocatedDevice[0]?.devices_assigned_date
        ? formatToUSDate(row.Device.AllocatedDevice[0].devices_assigned_date)
        : '-',
    key: 'Device?.AllocatedDevice[0]?.devices_assigned_date',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Allocated To Doctor At',
    selector: (row: any) => formatToUSDate(row.allocated_at),
    key: 'allocated_at',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
]

// Header for device list of the Doctor
// src/app/constants/RoleConstants.ts

export const DOCTOR_DEVICE_DETAILS_HEADERS = [
  {
    name: 'Device Name',
    selector: (row: any) => row.Device?.name,
    key: 'Device.name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'IMEI',
    selector: (row: any) => row.Device?.imei,
    key: 'Device.imei',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Ownership',
    selector: (row: any) => {
      const type = row.ownership_type || 'purchased'
      return type.charAt(0).toUpperCase() + type.slice(1)
    },
    key: 'ownership_type',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Assigned To',
    selector: (row: any) => row.Device?.AllocatedDevice?.[0]?.Patient?.full_name ?? '—',
    key: 'redirected_to',
    redirected_to: '/patient-details',
    secondaryKeyName: 'Device.AllocatedDevice[0].Patient.uuid',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Patient Status',
    selector: (row: any) => {
      const status = row.Device?.AllocatedDevice?.[0]?.Patient?.status
      if (status === 1) return 'Active'
      if (status === 0) return 'Inactive'
      return '—'
    },
    key: 'Device.AllocatedDevice[0].Patient.status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
  },
  {
    name: 'Actions',
    key: 'settings',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT],
  },
]

// Header for displaying all devices the super admin has
export const ADMIN_ALL_DEVICES_HEADERS = [
  {
    name: 'Device Name',
    selector: (row: any) => row.name,
    key: 'name',
    requiredPermission: 'public',
  },
  {
    name: 'IMEI',
    selector: (row: any) => row.imei,
    key: 'imei',
    requiredPermission: 'public',
  },
  {
    name: 'Sr No',
    selector: (row: any) => row.sr_no || '—',
    key: 'sr_no',
    requiredPermission: 'public',
  },
  {
    name: 'Device Type',
    selector: (row: any) =>
      `${row.DeviceType?.name === 'Blood Pressure Monitor' ? 'BPM' : row.DeviceType?.name}`,
    key: 'DeviceType?.name',
    requiredPermission: 'public',
  },
  {
    name: 'Assigned to Patient',
    selector: (row: any) => row.assigned_to_patient,
    key: 'assigned_to_patient_icon',
    requiredPermission: 'public',
  },
  {
    name: 'Ownership',
    key: 'device_allocation_status',
    sortable: false,
    requiredPermission: 'public',
    selector: (row: any) => {
      // Find the active allocation (where returned_at is null)
      const activeAllocation = row.DoctorAllocatedDevices?.find(
        (allocation: any) => allocation.returned_at === null
      )

      const ownershipType = activeAllocation?.ownership_type || 'purchased'

      const statusMap: Record<string, string> = {
        rental: 'Rented',
        purchased: 'Owned',
        loaned: 'Loaned',
      }

      const allocationData = {
        deviceId: row.id,
        status: activeAllocation
          ? (statusMap[ownershipType] || 'Owned')
          : 'Not Allocated',
        doctorAllocatedDeviceId: activeAllocation?.id,
        doctorId: activeAllocation?.doctor_id,
      }

      return allocationData
    },
  },

  {
    name: 'Allocated To',
    selector: (row: any) => {
      // Find the active allocation (where returned_at is null)
      const activeAllocation = row.DoctorAllocatedDevices?.find(
        (allocation: any) => allocation.returned_at === null
      )

      return activeAllocation?.User?.full_name || 'Not Allocated'
    },
    key: 'DoctorAllocatedDevices[0]?.User?.full_name',
    requiredPermission: 'public',
  },
  {name: 'Actions', key: 'settings', requiredPermission: 'public'},
]

// Header for displaying all requested devices of all doctors - for admin only
export const ADMIN_ALL_REQUESTED_DEVICES_HEADERS = [
  {
    name: 'Device Type',
    selector: (row: any) => row.DeviceType?.name,
    key: 'DeviceType?.name',
    requiredPermission: 'public',
  },
  {
    name: 'Request Status',
    selector: (row: any) => row.stkatus,
    key: 'request_status',
    requiredPermission: 'public',
  },
  {
    name: 'Number of Devices',
    selector: (row: any) => row.no_of_devices,
    key: 'no_of_devices',
    requiredPermission: 'public',
  },
  {
    name: 'Ownership Type',
    selector: (row: any) => {
      const type = row.ownership_type || 'purchased'
      return type.charAt(0).toUpperCase() + type.slice(1)
    },
    key: 'ownership_type',
    requiredPermission: 'public',
  },
  {
    name: 'Requested By Doctor',
    selector: (row: any) => `${row.Doctor.email}`,
    key: 'Doctor?.email',
    requiredPermission: 'public',
  },
  {name: 'Actions', key: 'settings', requiredPermission: 'public'},
]

// Header for displaying all requested devices of a doctor

// Header for displaying all requested devices
export const DOCTOR_ALL_REQUESTED_DEVICES_HEADERS = [
  {
    name: 'Device Type',
    selector: (row: any) => row.DeviceType?.name,
    key: 'DeviceType?.name',
    requiredPermission: 'public',
  },
  {
    name: 'Request Status',
    selector: (row: any) => row.status,
    key: 'request_status',
    requiredPermission: 'public',
  },
  {
    name: 'Number of Devices',
    selector: (row: any) => row.no_of_devices,
    key: 'no_of_devices',
    requiredPermission: 'public',
  },
  {
    name: 'Ownership Type',
    selector: (row: any) => {
      const type = row.ownership_type || 'purchased'
      return type.charAt(0).toUpperCase() + type.slice(1)
    },
    key: 'ownership_type',
    requiredPermission: 'public',
  },
  {
    name: 'Requested Date',
    selector: (row: any) =>
      row.created_at ? moment(row?.created_at).format('YYYY-MM-DD HH:mm') : '-',
    key: 'created_at',
    requiredPermission: 'public',
  },

  {
    name: 'Payment Status',
    selector: (row: any) =>
      row?.Transactions[0]?.status ? row?.Transactions[0]?.status : 'Pending',
    key: 'Transactions[0]?.status',
    requiredPermission: 'public',
  },

  {
    name: 'Amount',
    selector: (row: any) =>
      row?.Transactions[0]?.amount ? `$${row?.Transactions[0]?.amount}` : '-',
    key: 'Transactions[0]?.amount',
    requiredPermission: 'public',
  },
]

export const ALL_READINGS_OF_PATIENTS_HEADERS = [
  {
    name: 'Date',
    selector: (row: any) => formatBackendDate(row?.reading_taken_at) || '-',
    key: 'reading_taken_at',
    requiredPermission: 'public',
  },
  {
    name: 'Time',
    selector: (row: any) => formatBackendTime(row?.reading_taken_at) || '-',
    key: 'reading_taken_at',
    requiredPermission: 'public',
  },
  {
    name: 'SYS',
    selector: (row: any) => row?.sys || 'N/A',
    key: 'sys',
    requiredPermission: 'public',
  },
  {
    name: 'DIA',
    selector: (row: any) => row?.dia || 'N/A',
    key: 'dia',
    requiredPermission: 'public',
  },
  {
    name: 'HR',
    selector: (row: any) => row?.pulse || 'N/A',
    key: 'pulse',
    requiredPermission: 'public',
  },
  {
    name: 'BP Classification',
    selector: (row: any) => row?.bp_category || 'N/A',
    key: 'bp_classification',
    requiredPermission: 'public',
  },
  // {
  //   name: 'Notes',
  //   key: 'notes',
  //   requiredPermission: 'public',
  //   selector: (row: any) => row?.sys || 'N/A',
  // },
]

// doctor transaction headers(for dr and super-admin)
export const DOCTOR_TRANSACTION_HEADERS = [
  {
    name: 'Transaction Date',
    selector: (row: any) => moment.utc(row?.created_at).format('YYYY-MM-DD'),
    key: 'created_at',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Transaction By',
    selector: (row: any) => row?.User?.first_name,
    key: 'User?.first_name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Device Type',
    selector: (row: any) => row?.DeviceType?.name,
    key: 'DeviceType?.name',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Quantity',
    selector: (row: any) => row?.quantity,
    key: 'quantity',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Amount',
    selector: (row: any) => `$${row?.amount}`,
    key: 'amount',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
  {
    name: 'Payment Status',
    selector: (row: any) => row?.status,
    key: 'payment-status',
    allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE],
  },
]

//all  doctor transaction headers(for super-admin only)
export const ALL_DOCTOR_TRANSACTION_HEADERS = [
  // {
  //   name: 'ID',
  //   selector: (row: any) => row?.id,
  //   sortable: true,
  //   key: 'id',
  //   requiredPermission: 'public',
  // },
  {
    name: 'Transaction Date',
    selector: (row: any) => moment.utc(row?.created_at).format('YYYY-MM-DD'),
    key: 'created_at',
    requiredPermission: 'public',
  },
  {
    // name: 'Transaction By',
    // selector: (row: any) => row?.User?.first_name,
    // key: 'User?.first_name',
    // requiredPermission: 'public',

    name: 'Transaction By',
    key: 'redirected_to',
    secondaryKeyName: 'User.full_name',
    redirected_to: '/doctor-details',
    requiredPermission: 'public',
  },
  {
    name: 'Device Type',
    selector: (row: any) => row?.DeviceType?.name,
    key: 'DeviceType?.name',
    requiredPermission: 'public',
  },
  {
    name: 'Quantity',
    selector: (row: any) => row?.quantity,
    key: 'quantity',
    requiredPermission: 'public',
  },
  {
    name: 'Amount',
    selector: (row: any) => `$${row?.amount}`,
    key: 'amount',
    requiredPermission: 'public',
  },
  {
    name: 'Payment Status',
    selector: (row: any) => row?.status,
    key: 'payment-status',
    requiredPermission: 'public',
  },
]

export const DOCTORS_BILLING_REPORT_HEADERS = [
  {
    name: t.patient,
    key: 'patient',
    selector: (row: any) => row?.full_name,
    requiredPermission: 'public',
    minWidth: '200px',
    grow: 2,
  },
  {
    name: t.startCycle,
    selector: (row: any) => {
      if (!row?.cycle_start) return '---'
      // Backend sends billing cycle dates in CST, so format directly
      const date = formatBillingCycleDateToUser(row.cycle_start, 'MMM D')
      return date
    },
    key: 'start_cycle',
    requiredPermission: 'public',
  },
  {
    name: t.endCycle,
    selector: (row: any) => {
      if (!row?.cycle_end) return '---'
      // Backend sends billing cycle dates in CST, so format directly
      const date = formatBillingCycleDateToUser(row.cycle_end, 'MMM D')

      return date
    },
    key: 'end_cycle',
    requiredPermission: 'public',
  },
  {
    name: t.cpt99453,
    key: 'cpt_99453',
    selector: (row: any) => (row?.cpt_99453 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.readings,
    selector: (row: any) => row?.readings,
    key: 'readings',
    requiredPermission: 'public',
    width: '100px',
  },
  {
    name: t.cpt99445,
    key: 'cpt_99445',
    selector: (row: any) => (row?.cpt_99445 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.cpt99454,
    key: 'cpt_99454',
    selector: (row: any) => (row?.cpt_99454 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.careMins,
    selector: (row: any) => {
      const minutes = parseFloat(row?.care_minutes ?? 0)
      return isNaN(minutes) ? 0 : minutes
    },
    key: 'care_minutes',
    sortable: true,
    requiredPermission: 'public',
    width: '105px',
  },
  {
    name: t.cpt99470,
    key: 'cpt_99470',
    selector: (row: any) => (row?.cpt_99470 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.cpt99457,
    key: 'cpt_99457',
    selector: (row: any) => (row?.cpt_99457 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.cpt99458,
    key: 'cpt_99458',
    requiredPermission: 'public',
    selector: (row: any) => {
      return row?.cpt_99458
        ? `${row?.cpt_99458} unit${row?.cpt_99458 > 1 ? 's' : ''}`
        : t.ineligible
    },
    width: '110px',
  },
]

export const DOCTORS_BILLING_ENDING_SOON_HEADERS = [
  {
    name: t.patient,
    key: 'patient',
    selector: (row: any) => row?.full_name,
  },
  {
    name: t.endCycle,
    selector: (row: any) => {
      if (!row?.cycle_end) return '---'
      // Backend sends billing cycle dates in CST, so format directly
      const date = formatBillingCycleDateToUser(row.cycle_end, 'MMM D')

      return date
    },
    key: 'end_cycle',
    requiredPermission: 'public',
  },
  {
    name: t.readings,
    selector: (row: any) => row?.readings,
    key: 'readings',
    width: '100px',
  },
  {
    name: t.cpt99445,
    key: 'cpt_99445',
    selector: (row: any) => (row?.cpt_99445 ? t.eligible : t.ineligible),
    width: '110px',
  },
  {
    name: t.cpt99454,
    key: 'cpt_99454',
    selector: (row: any) => (row?.cpt_99454 ? t.eligible : t.ineligible),
    width: '110px',
  },
  {
    name: t.careMins,
    selector: (row: any) => row?.care_minutes,
    key: 'care_minutes',
    width: '105px',
  },
  {
    name: t.cpt99470,
    key: 'cpt_99470',
    selector: (row: any) => (row?.cpt_99470 ? t.eligible : t.ineligible),
    width: '110px',
  },
  {
    name: t.cpt99457,
    key: 'cpt_99457',
    selector: (row: any) => (row?.cpt_99457 ? t.eligible : t.ineligible),
    width: '110px',
  },
  {
    name: t.cpt99458,
    key: 'cpt_99458',
    selector: (row: any) => {
      return row?.cpt_99458
        ? `${row?.cpt_99458} unit${row?.cpt_99458 > 1 ? 's' : ''}`
        : t.ineligible
    },
    width: '110px',
  },
]

// Report Export CSV Headers
export const PATIENT_BILLING_LIST_HEADERS = [
  {
    name: t.startCycle,
    selector: (row: any) => {
      if (!row?.cycle_start) return '---'
      // Backend sends billing cycle dates in CST, so format directly
      const date = formatBillingCycleDateToUser(row.cycle_start, 'MMM D')
      return date
    },
    key: 'start_cycle',
    requiredPermission: 'public',
  },
  {
    name: t.endCycle,
    selector: (row: any) => {
      if (!row?.cycle_end) return '---'
      // Backend sends billing cycle dates in CST, so format directly
      const date = formatBillingCycleDateToUser(row.cycle_end, 'MMM D')
      return date
    },
    key: 'end_cycle',
    requiredPermission: 'public',
  },
  {
    name: t.cpt99453,
    key: 'cpt_99453',
    selector: (row: any) => row?.cpt_99453,
    requiredPermission: 'public',
    width: '110px',
  },

  {
    name: t.readings,
    selector: (row: any) => row?.readings,
    key: 'readings',
    requiredPermission: 'public',
    width: '100px',
  },
  {
    name: t.cpt99445,
    key: 'cpt_99445',
    selector: (row: any) => (row?.cpt_99445 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.cpt99454,
    key: 'cpt_99454',
    selector: (row: any) => row?.cpt_99454,
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.careMins,
    selector: (row: any) => {
      const timeStr = row?.care_minutes
      return `${timeStr}`
    },
    key: 'care_minutes',
    requiredPermission: 'public',
    width: '105px',
  },
  {
    name: t.cpt99470,
    key: 'cpt_99470',
    selector: (row: any) => (row?.cpt_99470 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.cpt99457,
    key: 'cpt_99457',
    selector: (row: any) => (row?.cpt_99457 ? t.eligible : t.ineligible),
    requiredPermission: 'public',
    width: '110px',
  },
  {
    name: t.cpt99458,
    key: 'cpt_99458',
    requiredPermission: 'public',
    selector: (row: any) => {
      return row?.cpt_99458
        ? `${row?.cpt_99458} unit${row?.cpt_99458 > 1 ? 's' : ''}`
        : t.ineligible
    },
    width: '110px',
  },
  {
    name: 'Detailed Report',
    key: 'detailed_report',
    allowedRoles: [USER_ROLES.DOCTOR, USER_ROLES.NURSE],
    sortable: false,
    exportable: false,
    width: '125px',
  },
]
